import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { PARTNER_METADATA } from '@web-platform/shared-universal-types/src/network/requestTypes';

import type { QueryResponseActionPartnerMetadataPayload } from '@web-platform/shared-universal-types/src/redux/actions/actions-shared-types';
import type {
  PartnerMetadata,
  PartnerMetadataState,
} from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/partnerMetadata/partnerMetadata';

export const INITIAL_STATE: PartnerMetadataState = { byId: {} };

type PartnerMetadataQueryResponseAction = {
  type: 'QUERY_RESPONSE_RECEIVED';
  error: boolean;
  payload: QueryResponseActionPartnerMetadataPayload;
};

type PartnerMetadataAction = PartnerMetadataQueryResponseAction | { type: string };

const isPartnerMetadataQueryResponseAction = (
  action: PartnerMetadataAction,
): action is PartnerMetadataQueryResponseAction =>
  action.type === QUERY_RESPONSE_RECEIVED &&
  'payload' in action &&
  action.payload.type === PARTNER_METADATA;

const byId = (
  state: PartnerMetadata,
  action: PartnerMetadataQueryResponseAction,
): PartnerMetadata => {
  switch (action.payload.type) {
    case PARTNER_METADATA: {
      const { response } = action.payload;

      if (!response.headers || !response.body?.partnerMetadata) {
        return state;
      }

      const { partnerMetadata } = response.body;

      const transformedPartnerMetadata = Object.keys(partnerMetadata).reduce(
        (newState: PartnerMetadata, agentId) => {
          const trustedLevel = partnerMetadata[agentId].partnerTrustData?.trustedLevel;

          if (trustedLevel) {
            return {
              ...newState,
              [agentId]: {
                trust_level: trustedLevel,
              },
            };
          }

          return newState;
        },
        {},
      );

      const filteredState = Object.keys(state).reduce((newState, agentId) => {
        if (transformedPartnerMetadata[agentId]) {
          return {
            ...newState,
            [agentId]: transformedPartnerMetadata[agentId],
          };
        }
        return newState;
      }, state);

      return {
        ...filteredState,
        ...transformedPartnerMetadata,
      };
    }

    default:
      return state;
  }
};

export const partnerMetadataReducer = (
  state: PartnerMetadataState = INITIAL_STATE,
  action: PartnerMetadataAction,
): PartnerMetadataState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      if (isPartnerMetadataQueryResponseAction(action)) {
        const newById = byId(state.byId, action);
        if (newById === state.byId) return state;
        return { byId: newById };
      }
      return state;
    }
    default:
      return state;
  }
};
